import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import AdminLayout from "@/components/feature/AdminLayout";
import AdminHeader from "@/components/feature/AdminHeader";
import LoadingState from "@/components/base/LoadingState";
import ErrorState from "@/components/base/ErrorState";
import ModuleGate from "@/components/base/ModuleGate";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import MusicModal, { type InvMusic } from "./components/MusicModal";

const GENRES = ["Todos", "Clásica", "Romántica", "Pop", "Jazz", "Electrónica", "Instrumental", "Religiosa", "Tropical", "Otro"];

export default function InvitationMusicPage() {
  const navigate = useNavigate();
  const [tenantId, setTenantId]   = useState<string | null>(null);
  const [music, setMusic]         = useState<InvMusic[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [search, setSearch]       = useState("");
  const [genreFilter, setGenreFilter] = useState("Todos");
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected]   = useState<InvMusic | null>(null);
  const [isNew, setIsNew]         = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const fetchMusic = useCallback(async (tid: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from("invitation_music")
        .select("*")
        .eq("tenant_id", tid)
        .order("sort_order")
        .order("name");
      if (err) throw new Error(err.message);
      setMusic((data ?? []) as InvMusic[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error cargando música.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const ok = await requireAuth();
      if (!ok) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchMusic(tid);
      else setLoading(false);
    };
    init();
    return () => { audioRef.current?.pause(); };
  }, [navigate, fetchMusic]);

  /* ── Play/stop ── */
  const handlePlay = (track: InvMusic) => {
    if (playingId === track.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    audioRef.current?.pause();
    const a = new Audio(track.music_url);
    a.play().catch(() => {});
    a.onended = () => setPlayingId(null);
    audioRef.current = a;
    setPlayingId(track.id);
  };

  /* ── Toggle active ── */
  const handleToggle = async (track: InvMusic) => {
    setTogglingId(track.id);
    const { data, error: err } = await supabase
      .from("invitation_music")
      .update({ is_active: !track.is_active, updated_at: new Date().toISOString() })
      .eq("id", track.id)
      .select()
      .maybeSingle();
    if (!err && data) setMusic((prev) => prev.map((m) => m.id === track.id ? data as InvMusic : m));
    setTogglingId(null);
  };

  /* ── Save (create/update) ── */
  const handleSave = async (data: Partial<InvMusic>) => {
    if (!tenantId) return;
    if (isNew) {
      const { data: created, error: err } = await supabase
        .from("invitation_music")
        .insert({ ...data, tenant_id: tenantId })
        .select()
        .maybeSingle();
      if (err) { Swal.fire({ title: "Error", text: err.message, icon: "error" }); return; }
      if (created) setMusic((prev) => [...prev, created as InvMusic]);
      Swal.fire({ title: "Agregada", text: "Canción agregada al catálogo.", icon: "success", timer: 1500, showConfirmButton: false });
    } else if (selected) {
      const { data: updated, error: err } = await supabase
        .from("invitation_music")
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq("id", selected.id)
        .select()
        .maybeSingle();
      if (err) { Swal.fire({ title: "Error", text: err.message, icon: "error" }); return; }
      if (updated) setMusic((prev) => prev.map((m) => m.id === (updated as InvMusic).id ? updated as InvMusic : m));
      Swal.fire({ title: "Guardado", icon: "success", timer: 1500, showConfirmButton: false });
    }
    setModalOpen(false);
  };

  /* ── Delete ── */
  const handleDelete = async (track: InvMusic) => {
    const result = await Swal.fire({
      title: "¿Eliminar canción?",
      text: `"${track.name}" será eliminada del catálogo.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef4444",
    });
    if (!result.isConfirmed || !tenantId) return;
    if (playingId === track.id) { audioRef.current?.pause(); setPlayingId(null); }
    const { error: err } = await supabase.from("invitation_music").delete().eq("id", track.id).eq("tenant_id", tenantId);
    if (err) { Swal.fire({ title: "Error", text: err.message, icon: "error" }); return; }
    setMusic((prev) => prev.filter((m) => m.id !== track.id));
    Swal.fire({ title: "Eliminada", icon: "success", timer: 1200, showConfirmButton: false });
  };

  /* ── Filter ── */
  const filtered = music.filter((m) => {
    const matchSearch = m.name.toLowerCase().includes(search.toLowerCase()) ||
      (m.artist ?? "").toLowerCase().includes(search.toLowerCase());
    const matchGenre = genreFilter === "Todos" || m.genre === genreFilter;
    return matchSearch && matchGenre;
  });

  const activeCount   = music.filter((m) => m.is_active).length;
  const inactiveCount = music.length - activeCount;

  return (
    <AdminLayout>
      <AdminHeader
        title="Música para Invitaciones"
        subtitle="Catálogo de canciones disponibles para el fondo de las invitaciones digitales"
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/admin/invitations"
              className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 border border-slate-200 text-sm px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
              Volver
            </Link>
            <Link
              to="/admin/invitations/templates"
              className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 border border-slate-200 text-sm px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-image-2-line"></i></span>
              Templates
            </Link>
            <button
              onClick={() => { setSelected(null); setIsNew(true); setModalOpen(true); }}
              className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></span>
              Agregar Canción
            </button>
          </div>
        }
      />

      <ModuleGate moduleId="digital_invitations">
        <div className="px-4 md:px-8 py-6">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[
              { label: "Total", value: music.length, icon: "ri-music-2-line", color: "text-slate-600", bg: "bg-slate-50" },
              { label: "Activas", value: activeCount, icon: "ri-checkbox-circle-line", color: "text-emerald-600", bg: "bg-emerald-50" },
              { label: "Inactivas", value: inactiveCount, icon: "ri-pause-circle-line", color: "text-slate-400", bg: "bg-slate-50" },
            ].map((s) => (
              <div key={s.label} className={`${s.bg} rounded-xl p-4 flex items-center gap-3`}>
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white">
                  <i className={`${s.icon} ${s.color} text-lg`}></i>
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0f172a]">{s.value}</p>
                  <p className="text-slate-400 text-xs">{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-5">
            <div className="relative flex-1 max-w-sm">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
                <i className="ri-search-line text-sm"></i>
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar canción o artista…"
                className="w-full border border-slate-200 rounded-lg pl-9 pr-4 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/50 transition-colors"
              />
            </div>
            {/* Genre filter pills */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {GENRES.map((g) => (
                <button
                  key={g}
                  onClick={() => setGenreFilter(g)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                    genreFilter === g
                      ? "bg-[#C8A96E] border-[#C8A96E] text-white"
                      : "border-slate-200 text-slate-400 hover:border-slate-300"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState message={error} onRetry={() => tenantId && fetchMusic(tenantId)} showLogin />
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-slate-200 rounded-2xl">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-slate-100 mx-auto mb-3">
                <i className="ri-music-line text-slate-400 text-2xl"></i>
              </div>
              <p className="text-slate-500 font-medium text-sm mb-1">
                {music.length === 0 ? "Sin canciones en el catálogo" : "Sin resultados"}
              </p>
              <p className="text-slate-400 text-xs mb-5">
                {music.length === 0
                  ? "Agrega canciones para que tus clientes puedan elegir música en sus invitaciones."
                  : "Prueba ajustando los filtros de búsqueda."}
              </p>
              {music.length === 0 && (
                <button
                  onClick={() => { setSelected(null); setIsNew(true); setModalOpen(true); }}
                  className="text-[#C8A96E] text-sm font-medium hover:underline cursor-pointer"
                >
                  + Agregar primera canción
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {/* Table header */}
              <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-4 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                <span className="w-10"></span>
                <span>Canción</span>
                <span className="hidden md:block w-24">Género</span>
                <span className="hidden md:block w-16">Duración</span>
                <span className="w-16 text-center">Estado</span>
                <span className="w-16 text-right">Acciones</span>
              </div>

              {filtered.map((track) => {
                const isPlaying = playingId === track.id;
                const isToggling = togglingId === track.id;
                return (
                  <div
                    key={track.id}
                    className={`grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-4 items-center px-4 py-3 bg-white rounded-xl border transition-all ${
                      isPlaying ? "border-[#C8A96E]/40 bg-amber-50/30" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    {/* Play button */}
                    <button
                      onClick={() => handlePlay(track)}
                      className={`w-10 h-10 flex items-center justify-center rounded-full border-2 transition-all cursor-pointer shrink-0 ${
                        isPlaying
                          ? "border-[#C8A96E] bg-[#C8A96E]/10 text-[#C8A96E]"
                          : "border-slate-200 text-slate-400 hover:border-[#C8A96E]/50 hover:text-[#C8A96E]"
                      }`}
                    >
                      {isPlaying ? (
                        <div className="flex gap-0.5 items-end">
                          {[1,2,3].map((i) => (
                            <div key={i} className="w-0.5 bg-[#C8A96E] rounded-full animate-pulse" style={{ height: `${6 + i * 3}px`, animationDelay: `${i * 0.12}s` }} />
                          ))}
                        </div>
                      ) : (
                        <i className="ri-play-fill text-sm"></i>
                      )}
                    </button>

                    {/* Info */}
                    <div className="min-w-0">
                      <p className={`text-sm font-medium truncate ${isPlaying ? "text-[#C8A96E]" : "text-[#0f172a]"}`}>{track.name}</p>
                      {track.artist && <p className="text-slate-400 text-xs truncate">{track.artist}</p>}
                    </div>

                    {/* Genre */}
                    <span className="hidden md:block w-24 text-xs text-slate-400 whitespace-nowrap">{track.genre ?? "—"}</span>

                    {/* Duration */}
                    <span className="hidden md:block w-16 text-xs text-slate-400 font-mono">{track.duration ?? "—"}</span>

                    {/* Toggle */}
                    <div className="w-16 flex items-center justify-center">
                      <button
                        onClick={() => handleToggle(track)}
                        disabled={isToggling}
                        className={`w-9 h-5 rounded-full transition-colors relative cursor-pointer disabled:opacity-50 ${track.is_active ? "bg-emerald-400" : "bg-slate-200"}`}
                      >
                        {isToggling ? (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                          </div>
                        ) : (
                          <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${track.is_active ? "translate-x-4" : "translate-x-0.5"}`} />
                        )}
                      </button>
                    </div>

                    {/* Actions */}
                    <div className="w-16 flex items-center justify-end gap-1">
                      <button
                        onClick={() => { setSelected(track); setIsNew(false); setModalOpen(true); }}
                        className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-[#C8A96E] hover:bg-amber-50 rounded-lg transition-all cursor-pointer"
                        title="Editar"
                      >
                        <i className="ri-pencil-line text-xs"></i>
                      </button>
                      <button
                        onClick={() => handleDelete(track)}
                        className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-red-50 rounded-lg transition-all cursor-pointer"
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-line text-xs"></i>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {modalOpen && (
          <MusicModal
            music={selected}
            isNew={isNew}
            tenantId={tenantId ?? ""}
            onClose={() => setModalOpen(false)}
            onSave={handleSave}
          />
        )}
      </ModuleGate>
    </AdminLayout>
  );
}